/**
 * WelcomeStampModal - Modal that shows welcome stamp to new users
 * Replaces the old WelcomePointsModal (5 points → 1 stamp)
 */

import { X } from 'lucide-react';

interface WelcomeStampModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName?: string;
}

export function WelcomeStampModal({
  isOpen,
  onClose,
  userName,
}: WelcomeStampModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-stamp-modal-title"
        className="bg-surface rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-scale-in"
      >
        {/* Header with gradient background */}
        <div className="relative p-6 text-center">
          {/* Close button */}
          <button
            onClick={onClose}
            aria-label="Cerrar"
            className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors z-10"
          >
            <X size={20} />
          </button>

          {/* Monkey stamp image */}
          <div className="mx-auto w-28 h-28 bg-white rounded-full flex items-center justify-center shadow-lg mb-4 p-2 relative">
            <img
              src="/logo/logo_gorilla_sonriendo-sin-fondo.webp"
              alt="Monkey Stamp"
              className="w-full h-full object-contain"
            />
            {/* Stamp badge */}
            <div className="absolute -bottom-1 -right-1 bg-success/100 text-white text-xs font-bold px-2 py-1 rounded-full shadow-md">
              +1
            </div>
          </div>

          {/* Title */}
          <h2 id="welcome-stamp-modal-title" className="text-2xl font-bold text-white mb-2">
            {userName ? `¡Hola ${userName}!` : '¡Bienvenido/a!'}
          </h2>
          <p className="text-accent text-sm">
            a Monkey Barbershop
          </p>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          {/* Stamp display */}
          <div className="mb-4">
            <p className="text-ink-muted text-sm mb-3">
              ¡Has recibido tu primer sello!
            </p>
            <div className="flex items-center justify-center gap-3 bg-accent/10 rounded-xl p-4">
              <img
                src="/logo/logo_gorilla_sonriendo-sin-fondo.webp"
                alt="Sello"
                className="w-12 h-12 object-contain"
              />
              <div className="text-left">
                <p className="text-2xl font-bold text-accent">
                  ¡SELLO DE BIENVENIDA!
                </p>
                <p className="text-xs text-ink-subtle">
                  Regalo por unirte a la familia
                </p>
              </div>
            </div>
          </div>

          {/* Progress info */}
          <div className="mb-6 bg-surface-2 rounded-lg p-3">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-ink-muted">Tu progreso</span>
              <span className="text-sm font-semibold text-accent">1/10 sellos</span>
            </div>
            <div className="w-full bg-surface-2 rounded-full h-2">
              <div
                className="h-2 rounded-full transition-all"
                style={{ width: '10%' }}
              />
            </div>
            <p className="text-xs text-ink-subtle mt-2">
              ¡Acumula sellos y consigue descuentos!
            </p>
          </div>

          {/* CTA button */}
          <button
            onClick={onClose}
            className="w-full py-3 px-6 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl"
          >
            ¡Empezar a acumular sellos!
          </button>
        </div>
      </div>
    </div>
  );
}
